import { computed } from '@vue/composition-api';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  IWorkflowStateThresholdGroup,
  IWorkflowStateThresholdGroupData,
  WorkflowStateThresholdGroup
} from '@/models/workflow';
import providerInstance from '@/services/provider';

// Split into CRUD composables

// Fetch all thresholdGroups
export function useFetchThresholdGroups(workflowId: number, stateId: number) {
  const getThresholdGroupsQueryKey = () => [
    'workflow',
    workflowId,
    'states',
    stateId,
    'threshold-groups'
  ];

  const {
    data: thresholdGroups,
    isLoading: isLoadingThresholdGroups,
    error: thresholdGroupsError,
    refetch: refetchThresholdGroups
  } = useQuery({
    queryKey: getThresholdGroupsQueryKey(),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowStateThresholdGroups(
        workflowId,
        stateId
      ),
    select: data =>
      data.map(
        thresholdGroup => new WorkflowStateThresholdGroup(thresholdGroup)
      )
  });

  return {
    thresholdGroups,
    isLoadingThresholdGroups,
    thresholdGroupsError,
    refetchThresholdGroups
  };
}

// Fetch single thresholdGroup
export function useFetchThresholdGroup(
  workflowId: number,
  stateId: number,
  thresholdGroupId: number
) {
  const getThresholdGroupQueryKey = () => [
    'workflow',
    workflowId,
    'states',
    stateId,
    'threshold-groups',
    thresholdGroupId
  ];

  const {
    data: thresholdGroup,
    isLoading: isLoadingThresholdGroup,
    error: thresholdGroupError
  } = useQuery({
    queryKey: getThresholdGroupQueryKey(),
    queryFn: () =>
      providerInstance.workflow.fetchWorkflowStateThresholdGroup(
        workflowId,
        stateId,
        thresholdGroupId
      ),
    enabled: !!thresholdGroupId,
    select: thresholdGroup => new WorkflowStateThresholdGroup(thresholdGroup)
  });

  return {
    thresholdGroup: computed(() => thresholdGroup.value),
    isLoadingThresholdGroup,
    thresholdGroupError
  };
}

// Create thresholdGroup
export function useCreateThresholdGroup(workflowId: number, stateId: number) {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isLoading, error, reset } = useMutation({
    mutationFn: (data: IWorkflowStateThresholdGroupData) =>
      providerInstance.workflow.createWorkflowStateThresholdGroup(
        workflowId,
        stateId,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'threshold-groups'
      ]);
    }
  });

  return {
    createThresholdGroup: mutate,
    createThresholdGroupAsync: mutateAsync,
    isCreatingThresholdGroup: computed(() => isLoading.value),
    createThresholdGroupError: computed(() => error.value),
    createThresholdGroupErrorReset: reset
  };
}

// Update thresholdGroup
export function useUpdateThresholdGroup(workflowId: number, stateId: number) {
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isLoading, error, reset } = useMutation({
    mutationFn: (data: IWorkflowStateThresholdGroupData) =>
      providerInstance.workflow.updateWorkflowStateThresholdGroup(
        workflowId,
        stateId,
        data
      ),
    onSuccess: (_, thresholdGroup) => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'threshold-groups'
      ]);
      if (thresholdGroup.id) {
        queryClient.invalidateQueries([
          'workflow',
          workflowId,
          'states',
          stateId,
          'threshold-groups',
          thresholdGroup.id
        ]);
      }
    }
  });

  return {
    updateThresholdGroup: mutate,
    updateThresholdGroupAsync: mutateAsync,
    isUpdatingThresholdGroup: computed(() => isLoading.value),
    updateThresholdGroupError: computed(() => error.value),
    updateThresholdGroupErrorReset: reset
  };
}

// Delete thresholdGroup mutation
export function useDeleteThresholdGroup(workflowId: number, stateId: number) {
  const queryClient = useQueryClient();

  const { reset, mutate, mutateAsync, isLoading, error } = useMutation({
    mutationFn: (thresholdGroupId: number) =>
      providerInstance.workflow.deleteWorkflowStateThresholdGroup(
        workflowId,
        stateId,
        thresholdGroupId
      ),
    onSuccess: (_, thresholdGroupId) => {
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'threshold-groups'
      ]);
      queryClient.invalidateQueries([
        'workflow',
        workflowId,
        'states',
        stateId,
        'threshold-groups',
        thresholdGroupId
      ]);
    }
  });

  return {
    deleteThresholdGroupErrorReset: reset,
    deleteThresholdGroup: mutate,
    deleteThresholdGroupAsync: mutateAsync,
    isDeletingThresholdGroup: computed(() => isLoading.value),
    deleteThresholdGroupError: computed(() => error.value)
  };
}
